/**
 * The split screen that opens every page: paper on the left (the front),
 * night on the right (the back), a caption cut by the seam and a banner
 * sitting on it.
 *
 * Everything the scroll moves has its own wrapper, so the arrival (after
 * the loader) and the scroll never animate the same property of the same
 * element. The markup is the server's (the `split` macro of
 * templates/site/components/_ui.html.twig); this module moves it.
 *
 * @module components/split
 */

import { REDUCED_MOTION } from '../config.js';
import { gsap } from '../lib/gsap.js';

/**
 * The arrival: the halves slam together, the captions rise, the banner pops
 * and the terminal types. Built at boot, paused: from() tweens render their
 * start state on creation, so the halves are already off screen when the
 * loader leaves — nothing is ever seen in place and then snapped back.
 *
 * @returns {gsap.core.Timeline | null} Played by the loader.
 */
export function createSplitIntro() {
    const root = document.querySelector('.split');

    if (!root) {
        return null;
    }

    const timeline = gsap.timeline({ paused: true });

    /**
     * @param {Element | NodeList | null} targets
     * @param {gsap.TweenVars} vars
     * @param {number} at
     */
    const step = (targets, vars, at) => {
        if (targets && !(targets instanceof NodeList && targets.length === 0)) {
            timeline.from(targets, vars, at);
        }
    };

    step(
        root.querySelector('[data-half="front"] [data-half-inner]'),
        { xPercent: -101, duration: 1.15, ease: 'power4.out' },
        0,
    );
    step(
        root.querySelector('[data-half="back"] [data-half-inner]'),
        { xPercent: 101, duration: 1.15, ease: 'power4.out' },
        0,
    );
    step(
        root.querySelectorAll('[data-cap="open"] .caption__line > span'),
        { yPercent: 110, duration: 1, stagger: 0.08 },
        0.55,
    );
    step(
        root.querySelector('[data-banner-inner]'),
        { scale: 0.4, opacity: 0, duration: 0.9, ease: 'back.out(1.8)' },
        0.8,
    );
    step(
        root.querySelectorAll('[data-half="back"] [data-terminal-body] > span'),
        { opacity: 0, duration: 0.05, stagger: 0.07, ease: 'none' },
        0.9,
    );
    step(root.querySelectorAll('[data-split-hint] > *'), { opacity: 0, y: 10, duration: 0.6, stagger: 0.05 }, 1.15);

    // The story types its name card later, on scroll.
    if (!root.hasAttribute('data-split-pin')) {
        step(root.querySelector('[data-name-text]'), { opacity: 0, y: 12, duration: 0.7 }, 1.05);
    }

    if (REDUCED_MOTION) {
        timeline.progress(1);
    }

    return timeline;
}

/**
 * Inner pages: the halves part and the banner fades as the opening scrolls
 * away.
 *
 * @param {HTMLElement} root - The split section.
 */
export function animateSplitAway(root) {
    if (REDUCED_MOTION || !root) {
        return;
    }

    const timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: true },
    });

    const to = (targets, vars) => {
        if (targets && !(targets instanceof NodeList && targets.length === 0)) {
            timeline.to(targets, vars, 0);
        }
    };

    to(root.querySelector('[data-half="front"]'), { xPercent: -45 });
    to(root.querySelector('[data-half="back"]'), { xPercent: 45 });
    to(root.querySelector('[data-banner]'), { opacity: 0, scale: 0.8, duration: 0.5 });
    to(root.querySelectorAll('[data-split-hint], .caption, [data-name]'), { opacity: 0, duration: 0.45 });
}
